import React, { useEffect, useCallback } from "react";
import { Alert } from "reactstrap";
import { isReadOnly } from "../../helpers/rbac_helper";
import { notifyReadOnlyBlocked } from "../../helpers/readOnlyNotify";
import { usePermissions } from "../Hooks/usePermissions";

const HIDDEN_CLASS = "readonly-action-hidden";

const SAFE_CONTROL_TEXT =
  /^(search|filter|apply|clear|reset|export|download|refresh|close|cancel|back|next|prev|previous|ok|view|details|show|hide|more|less|logout|log out|profile|confirm logout)$/i;

const MUTATION_CONTROL_TEXT =
  /^(add(\s|$)|create|new\s|edit|update|delete|remove|save|submit|approve|reject|confirm|send|upload|assign|credit|debit|yes,?\s*delete|block|unblock|enable|disable|switch(\s|$)|change(\s|$))/i;

const MUTATION_ICON_SELECTOR = [
  ".ri-add-line",
  ".ri-pencil-line",
  ".ri-edit-line",
  ".ri-edit-2-line",
  ".ri-delete-bin-line",
  ".ri-delete-bin-2-line",
  ".ri-delete-bin-fill",
  ".ri-close-circle-line",
  ".ri-upload-2-line",
  ".ri-upload-cloud-line",
  ".ri-save-line",
  ".ri-send-plane-line",
].join(", ");

const CONTROL_SELECTOR =
  'button, a.btn, [role="button"], input[type="submit"], input[type="button"], .dropdown-item';

const isSafeNavRegion = (el) =>
  Boolean(
    el.closest(
      "#page-topbar, .navbar-menu, .app-menu, .navbar-header, .footer, .pagination, .nav-tabs, .nav-pills, .topbar-user, .profile-dropdown, [data-readonly-allow]"
    )
  );

const getControlText = (el) =>
  (el.textContent || el.getAttribute("aria-label") || "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Heuristic: is this control likely a create/update/delete action?
 * Filters, search, pagination, and navigation are left alone.
 */
export const isLikelyMutationControl = (target) => {
  if (!target || !(target instanceof Element)) return false;

  const control = target.matches?.(CONTROL_SELECTOR)
    ? target
    : target.closest(CONTROL_SELECTOR);
  if (!control) return false;
  if (control.hasAttribute("data-readonly-allow")) return false;
  if (isSafeNavRegion(control)) return false;

  const text = getControlText(control);
  if (SAFE_CONTROL_TEXT.test(text)) return false;

  if (
    control.matches('input[type="submit"]') ||
    control.getAttribute("type") === "submit"
  ) {
    if (SAFE_CONTROL_TEXT.test(text)) return false;
    if (!control.closest(".modal")) {
      const form = control.closest("form");
      if (form && /filter|search/i.test(form.className || "")) return false;
    }
    return true;
  }

  if (control.closest(".modal-footer")) {
    if (SAFE_CONTROL_TEXT.test(text) || /^(no|discard)$/i.test(text)) {
      return false;
    }
    return true;
  }

  if (control.querySelector(MUTATION_ICON_SELECTOR)) {
    if (control.disabled || control.getAttribute("aria-disabled") === "true") {
      return false;
    }
    return true;
  }

  if (MUTATION_CONTROL_TEXT.test(text)) return true;

  // Row-level status change controls inside tables
  if (
    control.closest("td, tbody, .table") &&
    /^(approved|rejected|pending|active|inactive|blocked|enabled|disabled)$/i.test(
      text
    )
  ) {
    return true;
  }

  return false;
};

const hideWriteControls = (root = document) => {
  if (!root?.querySelectorAll) return;

  root.querySelectorAll(CONTROL_SELECTOR).forEach((el) => {
    if (isLikelyMutationControl(el)) {
      el.classList.add(HIDDEN_CLASS);
      el.setAttribute("aria-hidden", "true");
      el.setAttribute("tabindex", "-1");
    }
  });
};

/**
 * Global UI guard + banner for readOnly role.
 * Hides write-action controls; API layer still blocks mutations as a safety net.
 */
const ReadOnlyMode = () => {
  const { isReadOnly: readOnly } = usePermissions();

  useEffect(() => {
    if (!readOnly) {
      document.documentElement.removeAttribute("data-readonly");
      document
        .querySelectorAll(`.${HIDDEN_CLASS}`)
        .forEach((el) => {
          el.classList.remove(HIDDEN_CLASS);
          el.removeAttribute("aria-hidden");
          el.removeAttribute("tabindex");
        });
      return undefined;
    }

    document.documentElement.setAttribute("data-readonly", "true");
    hideWriteControls(document);

    let scheduled = false;
    const scheduleHide = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        if (isReadOnly()) hideWriteControls(document);
      });
    };

    const observer = new MutationObserver(scheduleHide);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Silent safety net if any write control was missed by hide logic
    const blockEvent = (event) => {
      if (!isReadOnly()) return;
      if (!isLikelyMutationControl(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
    };

    const blockSubmit = (event) => {
      if (!isReadOnly()) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest("[data-readonly-allow]")) return;
      if (/filter|search/i.test(form.className || form.id || "")) return;
      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener("click", blockEvent, true);
    document.addEventListener("submit", blockSubmit, true);

    return () => {
      observer.disconnect();
      document.documentElement.removeAttribute("data-readonly");
      document.removeEventListener("click", blockEvent, true);
      document.removeEventListener("submit", blockSubmit, true);
      document.querySelectorAll(`.${HIDDEN_CLASS}`).forEach((el) => {
        el.classList.remove(HIDDEN_CLASS);
        el.removeAttribute("aria-hidden");
        el.removeAttribute("tabindex");
      });
    };
  }, [readOnly]);

  if (!readOnly) return null;

  return (
    <div className="readonly-banner-wrap px-3 pt-3">
      <Alert color="warning" className="mb-0 py-2 readonly-banner">
        <i className="ri-eye-line me-2 align-middle" />
        You are signed in with <strong>read-only</strong> access. You can view
        pages but cannot create, update, or delete data.
      </Alert>
    </div>
  );
};

/**
 * Conditionally render children only when the user can edit.
 * Use for page-level action buttons when you want them hidden (not just blocked).
 */
export const GuardAction = ({ children, fallback = null }) => {
  const { canEdit } = usePermissions();
  if (!canEdit) return fallback;
  return children;
};

/**
 * Disable interactive props for read-only users.
 */
export const useReadOnlyDisabled = (disabled = false) => {
  const { canEdit } = usePermissions();
  return disabled || !canEdit;
};

/**
 * Wrap a handler so read-only users get a toast instead of running it.
 */
export const useReadOnlySafeHandler = (handler) => {
  const { canEdit } = usePermissions();

  return useCallback(
    (...args) => {
      if (!canEdit) {
        notifyReadOnlyBlocked();
        return undefined;
      }
      return handler?.(...args);
    },
    [canEdit, handler]
  );
};

export default ReadOnlyMode;
